import { useEffect, useState } from 'react';
import {
  EmployeePermission,
  PermissionEditor,
  RolePermission,
  PermissionEditorEmployee,
  Permission,
  LoadingPanel,
  hasValue,
  PermissionAccessLevel,
} from '@amsanalytics/react-shared-library';
import styles from './_permission-editor-page.module.scss';
import { useSelector } from 'react-redux';
import { isEmpty } from 'ramda';
import { AppState } from '../../store/rootReducer';
import { httpGetAsync, httpPostAsync } from '../../utils/HttpClient';
import { authService } from '../..';
import { displayMessage } from '../../utils/utils';

export default function PermissionEditorPage() {
  // List of employees to edit in the permission editor
  const [employees, setEmployees] = useState<PermissionEditorEmployee[]>([]);
  const employeeRoles = useSelector((state: AppState) => state.applications.employeeRoles);
  const [permissions, setPermissions] = useState<Permission[]>([]);
  const [rolePermissions, setRolePermissions] = useState<RolePermission[]>([]);
  // ID of the employee we are loading data for
  const [employeeBeingLoadedId, setEmployeeBeingLoadedId] = useState<string | undefined>();

  // Async GET method to get permission editor data
  const fetchPermissionEditorData = async () => {
    try {
      const bearerToken = await authService.getToken();
      // First check that all of the necessary data is on the opportunity as these fields are required by Finicity
      const response = await httpGetAsync(`api/permission`, bearerToken);
      if (response && (response as Response).ok) {
        // destructure the "data" from the response.
        // data should have shape { employees: PermissionEditorEmployee[], permissions: Permission[], rolePermissions: RolePermission[] }
        const { employees, permissions, rolePermissions } = await response.json();
        setEmployees(employees);
        setPermissions(permissions);
        setRolePermissions(rolePermissions);
      } else if (response) {
        displayMessage(`${response.status}: ${response.statusText}`, 'error');
      }
    } catch (error) {
      console.error('Error fetching permission editor data:', error);
    }
  };

  // Async GET method to load the selected employee's roles and permissions.
  const loadEmployeePermissions = async (employeeId: string) => {
    const token: string = await authService.getToken();
    const response = await httpGetAsync(`api/permission/employee/${employeeId}`, token);
    if (response && (response as Response).ok) {
      const { employeeRoleIds, employeePermissions } = await response.json();
      // Add all permissions to the employee, even if they are not assigned to the employee.
      // The ones not assigned will have canView, canUpdate, canCreate, and canDelete set to false.
      // and permissionAccessLevel set to Across. if they are assigned, they will have the original values from the employeePermissions.
      const permissionsForSelectedEmployee: EmployeePermission[] = permissions.map((permission) => {
        const employeePermission = employeePermissions.find(
          (p: EmployeePermission) => p.id.toUpperCase() === permission.id.toUpperCase()
        );
        return {
          id: permission.id,
          name: permission.name,
          description: permission.description,
          canView: employeePermission?.canView ?? false,
          canUpdate: employeePermission?.canUpdate ?? false,
          canCreate: employeePermission?.canCreate ?? false,
          canDelete: employeePermission?.canDelete ?? false,
          permissionAccessLevel: employeePermission?.permissionAccessLevel ?? PermissionAccessLevel.Across,
        };
      });
      setEmployees((prevEmployees) =>
        prevEmployees.map((employee) =>
          employee.id === employeeId
            ? {
                ...employee,
                employeeRoleIds,
                employeePermissions: permissionsForSelectedEmployee,
              }
            : employee
        )
      );
    } else if (response) {
      displayMessage(`${response.status}: ${response.statusText}`, 'error');
    }
    setEmployeeBeingLoadedId(undefined);
  };

  // Async POST method to update an employeePermission. The server will handle the Create/Update/Deletion logic.
  // Server returns 200 (no content) on success.
  const updateEmployeePermission = async (employeeId: string, employeePermission: EmployeePermission) => {
    const token: string = await authService.getToken();
    const response = await httpPostAsync(
      `api/permission/employee/${employeeId}`,
      employeePermission,
      token
    );

    if (response && response.status === 200) {
      setEmployees((prevEmployees) =>
        prevEmployees.map((employee) =>
          employee.id === employeeId
            ? {
                ...employee,
                employeePermissions: employee.employeePermissions.map((permission) =>
                  permission.id.toUpperCase() === employeePermission.id.toUpperCase() ? employeePermission : permission
                ),
              }
            : employee
        )
      );
    } else if (response) {
      displayMessage(`${response.status}: ${response.statusText}`, 'error');
    }
  };

  // Async POST method to update a role permission. The server will handle the Create/Update/Delete operations.
  // Server returns 200 on success.
  const updateRolePermission = async (rolePermission: RolePermission) => {
    const token: string = await authService.getToken();
    const response = await httpPostAsync(`api/permission/role`, rolePermission, token);

    if (response && response.status === 200) {
      setRolePermissions((prevRolePermissions) =>
        prevRolePermissions.map((rp) => (rp.id.toUpperCase() === rolePermission.id.toUpperCase() ? rolePermission : rp))
      );
    } else if (response) {
      displayMessage(`${response.status}: ${response.statusText}`, 'error');
    }
  };

  // on initial load, fetch permission editor data
  useEffect(() => {
    fetchPermissionEditorData();
  }, []);

  return (
    <div className={styles.permissionEditorPageContainer}>
      {(isEmpty(employees) || isEmpty(employeeRoles)) && (
        <LoadingPanel
          label='Loading permissions...'
          style={{
            position: 'absolute',
            left: 0,
            right: 0,
            top: '48px',
            bottom: 0,
            zIndex: 3001,
          }}
        />
      )}
      {hasValue(employeeBeingLoadedId) && (
        <LoadingPanel
          label={`Loading ${employees.find((employee) => employee.id === employeeBeingLoadedId)?.fullName}'s roles and permissions...`}
          style={{
            position: 'absolute',
            left: 0,
            right: 0,
            top: '48px',
            bottom: 0,
            zIndex: 3001,
          }}
        />
      )}
      {!isEmpty(employees) && !isEmpty(employeeRoles) && (
        <PermissionEditor
          employees={employees}
          employeeRoles={employeeRoles.map((role) => ({
            id: role.id,
            name: role.name,
            description: role.description,
            sortOrder: 0,
            allowReportsTo: role.allowReportsTo,
          }))}
          permissions={permissions}
          rolePermissions={rolePermissions}
          onSelectEmployee={(employeeId: string) => {
            // If employee has no employeeRoleIds and no employeePermissions, load them.
            const employee = employees.find((employee) => employee.id === employeeId);
            if (isEmpty(employee?.employeeRoleIds) && isEmpty(employee?.employeePermissions)) {
              setEmployeeBeingLoadedId(employeeId);
              loadEmployeePermissions(employeeId);
            }
            setEmployees((prevEmployees) =>
              prevEmployees.map((employee) =>
                employee.id === employeeId ? { ...employee, selected: !employee.selected } : { ...employee, selected: false }
              )
            );
          }}
          onUpdateEmployeePermission={(employeeId: string, permission: EmployeePermission) => {
            updateEmployeePermission(employeeId, permission);
          }}
          onUpdateRolePermission={(permission: RolePermission) => {
            updateRolePermission(permission);
          }}
        />
      )}
    </div>
  );
}
